import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Platform } from "@-/com.npmjs.react-native";
import type { Tagged } from "@-/com.npmjs.type-fest";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { useDeviceAccount, useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useTraditionalCharactersEnabled } from "@-/dev.improve.haniverse.lib.posthog";
import { hasCompletedFirstLevelAndReviews } from "#first-level-account-milestone";

export type FirstLevelAccountPromptGate = Tagged<
  {
    /** Records the dismissal, which closes this gate (so the dialog unmounts). */
    dismiss: () => void;
  },
  "FirstLevelAccountPromptGate"
>;

/** The first bundled level's characters. The bundled level list is never empty. */
function getFirstLevelCharacters(traditional: boolean) {
  const [firstLevel] = traditional ? getTraditionalLevels() : getSimplifiedLevels();
  if (firstLevel === undefined) {
    throw new Error(
      `The bundled ${traditional ? "traditional" : "simplified"} grapheme levels are empty; the first level must exist`,
    );
  }
  return firstLevel.characters;
}

/**
 * Whether the "create your account" dialog shows: a focused, signed-out guest who hasn't dismissed
 * it, has no account email, and has learned the whole first level and then reviewed its cards.
 * `null` is the designed hidden state. Only web can have such a guest: the native app is not
 * entered without an account email (`AppLocalAccountGate`).
 *
 * The progress queries only run for such a guest. While they're pending (or if they fail) the gate
 * stays closed: this is a nudge, so "not proven eligible yet" and "not eligible" both mean hidden.
 */
export function useFirstLevelAccountPromptGate(): FirstLevelAccountPromptGate | null {
  const session = getAuthClient().useSession();
  const [deviceAccount] = useDeviceAccount();
  const isFocused = useIsFocused();
  const [dismissed, setDismissed] = useDeviceSetting("hasDismissedFirstLevelAccountPrompt");
  const [accountEmail] = useDeviceSetting("accountEmail");
  // On native, an address already recorded by "Get Started" means the account step is done; the
  // remaining nudge (verify it) lives on the profile, not in a second dialog here. Web has no such
  // anonymous account, so an address the browser last used does not count.
  const hasAnonymousAccount = Platform.OS !== "web" && accountEmail != null;
  const isGuestCandidate =
    isFocused &&
    !deviceAccount &&
    !session.isPending &&
    !session.data?.user &&
    !dismissed &&
    !hasAnonymousAccount;

  const traditional = useTraditionalCharactersEnabled();
  const characters = getFirstLevelCharacters(traditional);
  const cardsOptions = actorQueries(import.meta.location).userCard.findMany.useQueryOptions({
    where: {
      OR: [
        { userGraphemeMeaningCard: { grapheme: { in: characters } } },
        { userGraphemePronunciationCard: { grapheme: { in: characters } } },
      ],
    },
    with: { fsrsCard: true, userGraphemeMeaningCard: true, userGraphemePronunciationCard: true },
  });
  const cards = useQuery({ ...cardsOptions, enabled: isGuestCandidate });
  const user = useQuery({
    ...actorQueries(import.meta.location).user.findFirst.useQueryOptions({}),
    enabled: isGuestCandidate,
  });

  if (!isGuestCandidate || !cards.isSuccess || !user.isSuccess) {
    return null;
  }
  const isEligible = hasCompletedFirstLevelAndReviews({
    cards: cards.data,
    characters,
    // A fresh guest can have no user row yet; pinyin flashcards are then off.
    pinyinEnabled: user.data?.isPinyinFlashcardsEnabled ?? false,
  });
  return isEligible
    ? createGate<FirstLevelAccountPromptGate>({
        dismiss: () => {
          setDismissed(true);
        },
      })
    : null;
}
